// @ts-nocheck
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ReactionAllowance {
  reactionType: string;
  weeklyLimit: number;
  usedThisWeek: number;
  remaining: number;
}

const REACTION_LIMITS: Record<string, number> = {
  fire: -1, // unlimited
  trophy: 3,
  flowers: 5,
  grind: 5,
  brain: 5,
  teammate: 5,
  clutch: 3,
  lockdown: 5,
  levelup: 5,
};

export const useReactionAllowances = (profileId?: string) => {
  const [allowances, setAllowances] = useState<Record<string, ReactionAllowance>>({});
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const fetchAllowances = useCallback(async () => {
    if (!profileId) {
      setIsLoading(false);
      return;
    }

    try {
      const weekStart = getWeekStart();
      
      const { data, error } = await supabase
        .from('wall_reaction_allowances')
        .select('reaction_type, weekly_limit, used_this_week')
        .eq('profile_id', profileId)
        .eq('week_start', weekStart);

      if (error) throw error;

      // Build allowances map with defaults for types not yet in DB
      const allowanceMap: Record<string, ReactionAllowance> = {};
      
      Object.entries(REACTION_LIMITS).forEach(([type, limit]) => {
        const existing = data?.find(a => a.reaction_type === type);
        allowanceMap[type] = {
          reactionType: type,
          weeklyLimit: limit,
          usedThisWeek: existing?.used_this_week || 0,
          remaining: limit === -1 ? -1 : limit - (existing?.used_this_week || 0),
        };
      });

      setAllowances(allowanceMap);
    } catch (err) {
      console.error('Error fetching allowances:', err);
    } finally {
      setIsLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    fetchAllowances();
  }, [fetchAllowances]);

  const checkAllowance = useCallback((reactionType: string): boolean => {
    const allowance = allowances[reactionType];
    if (!allowance) return true; // Unknown type, allow it
    if (allowance.weeklyLimit === -1) return true; // Unlimited
    return allowance.remaining > 0;
  }, [allowances]);

  const useAllowance = useCallback(async (reactionType: string): Promise<boolean> => {
    if (!profileId) return false;
    
    const allowance = allowances[reactionType];
    if (allowance && allowance.weeklyLimit !== -1 && allowance.remaining <= 0) {
      toast({
        title: "Reaction limit reached",
        description: `You've used all your ${getEmojiForType(reactionType)} this week. Resets Monday!`,
        variant: "destructive",
      });
      return false;
    }

    try {
      const { data, error } = await supabase.rpc('use_reaction_allowance', {
        p_profile_id: profileId,
        p_reaction_type: reactionType,
      });

      if (error) throw error;
      
      if (!data) {
        toast({
          title: "Reaction limit reached",
          description: `You've used all your ${getEmojiForType(reactionType)} this week. Resets Monday!`,
          variant: "destructive",
        });
        return false;
      }

      // Update local state
      setAllowances(prev => {
        const current = prev[reactionType];
        if (!current || current.weeklyLimit === -1) return prev;
        return {
          ...prev,
          [reactionType]: {
            ...current,
            usedThisWeek: current.usedThisWeek + 1,
            remaining: current.remaining - 1,
          },
        };
      });

      return true;
    } catch (err) {
      console.error('Error using allowance:', err);
      return true; // Allow on error to not block reactions
    }
  }, [profileId, allowances, toast]);

  const restoreAllowance = useCallback(async (reactionType: string) => {
    if (!profileId) return;

    try {
      await supabase.rpc('restore_reaction_allowance', {
        p_profile_id: profileId,
        p_reaction_type: reactionType,
      });

      // Update local state
      setAllowances(prev => {
        const current = prev[reactionType];
        if (!current || current.weeklyLimit === -1) return prev;
        return {
          ...prev,
          [reactionType]: {
            ...current,
            usedThisWeek: Math.max(0, current.usedThisWeek - 1),
            remaining: Math.min(current.weeklyLimit, current.remaining + 1),
          },
        };
      });
    } catch (err) {
      console.error('Error restoring allowance:', err);
    }
  }, [profileId]);

  return {
    allowances,
    isLoading,
    checkAllowance,
    useAllowance,
    restoreAllowance,
    refetch: fetchAllowances,
  };
};

function getWeekStart(): string {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Monday
  const monday = new Date(now.setDate(diff));
  return monday.toISOString().split('T')[0];
}

function getEmojiForType(type: string): string {
  const emojiMap: Record<string, string> = {
    fire: '🔥',
    trophy: '🏆',
    flowers: '🌸',
    grind: '💪',
    brain: '🧠',
    teammate: '🤝',
    clutch: '🎯',
    lockdown: '🧱',
    levelup: '🚀',
  };
  return emojiMap[type] || '❓';
}
